import * as THREE from 'three';
import { SEA } from './style.js';

// Geometries are shared by every explosion.
const GEOMETRY = {
  droplet: new THREE.IcosahedronGeometry(0.22, 0),
  plume: new THREE.IcosahedronGeometry(0.9, 1),
  ring: new THREE.RingGeometry(0.8, 1, 40, 1).rotateX(-Math.PI / 2),
  fireball: new THREE.IcosahedronGeometry(1.2, 1),
  ember: new THREE.TetrahedronGeometry(0.3, 0),
  smoke: new THREE.IcosahedronGeometry(1, 0),
  plank: new THREE.BoxGeometry(0.9, 0.12, 0.25)
};

const EMBER_COLORS = [0xffd36b, 0xffb03a, 0xff8a1f, 0xff6a10, 0xfff0b0];
const PLANK_MATERIAL = new THREE.MeshLambertMaterial({ color: SEA.wood, flatShading: true });

// Fire is drawn with normal blending so it still reads against bright daylight
function glowMaterial(color, opacity = 1) {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, toneMapped: false });
}

function waterMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false });
}

// On the water: a column of spray, droplets raining back down, a foam ring
// racing outwards and splintered planks. In the air: a fireball with embers
// and smoke.
export class Explosion {
  constructor(scene, position, onComplete) {
    this.scene = scene;
    this.position = new THREE.Vector3().copy(position);
    this.onComplete = onComplete;
    this.onWater = this.position.y < 1.5;
    this.objects = [];
    this.particles = [];
    this.smoke = [];
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    this.duration = this.onWater ? 1200 : 1000;
    this.isComplete = false;
    if (this.onWater) {
      this.createSplash();
    } else {
      this.createBurst();
    }
  }

  add(object) {
    this.scene.add(object);
    this.objects.push(object);
    return object;
  }

  particle(geometry, material, velocity, spin = 8) {
    const mesh = this.add(new THREE.Mesh(geometry, material));
    mesh.position.copy(this.position);
    mesh.userData.velocity = velocity;
    mesh.userData.spin = new THREE.Vector3((Math.random() - 0.5) * spin, (Math.random() - 0.5) * spin, (Math.random() - 0.5) * spin);
    this.particles.push(mesh);
    return mesh;
  }

  createSplash() {
    const p = this.position;
    // Splashes always rise from the surface, whatever height the unit was at
    const sea = 0;
    this.surface = sea;

    // Spray droplets thrown mostly upwards
    this.dropletMaterial = waterMaterial(0xe8fbff, 0.95);
    for (let i = 0; i < 50; i++) {
      const a = Math.random() * Math.PI * 2;
      const out = 1.5 + Math.random() * 5;
      const drop = this.particle(GEOMETRY.droplet, this.dropletMaterial, new THREE.Vector3(Math.cos(a) * out, 9 + Math.random() * 9, Math.sin(a) * out), 4);
      drop.position.set(p.x + Math.cos(a) * 0.6, sea + 0.2, p.z + Math.sin(a) * 0.6);
      drop.scale.setScalar(0.7 + Math.random() * 1.1);
      drop.userData.water = true;
    }

    // The column itself: a stack of foamy blobs thrown up and slumping back
    this.plumeMaterial = new THREE.MeshLambertMaterial({ color: 0xf2fcff, emissive: 0x6a8a90, transparent: true, opacity: 0.95, flatShading: true, depthWrite: false });
    this.plumes = [];
    for (let i = 0; i < 9; i++) {
      const blob = this.add(new THREE.Mesh(GEOMETRY.plume, this.plumeMaterial));
      blob.position.set(p.x + (Math.random() - 0.5) * 0.8, sea, p.z + (Math.random() - 0.5) * 0.8);
      blob.userData.lift = 3 + i * 0.9 + Math.random() * 1.5;
      blob.userData.size = 1.3 - i * 0.08 + Math.random() * 0.3;
      blob.rotation.set(Math.random() * 3, Math.random() * 3, 0);
      this.plumes.push(blob);
    }

    // Foam rings racing across the water
    this.rings = [0, 0.18].map(delay => {
      const ring = this.add(new THREE.Mesh(GEOMETRY.ring, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide })));
      ring.position.set(p.x, sea + 0.08, p.z);
      ring.userData.delay = delay;
      return ring;
    });

    // A brief flash and splintered planks from whatever was hit
    this.flash = this.add(new THREE.Mesh(GEOMETRY.fireball, glowMaterial(0xffa23a, 0.9)));
    this.flash.position.set(p.x, sea + 1, p.z);
    for (let i = 0; i < 6; i++) {
      const a = Math.random() * Math.PI * 2;
      this.particle(GEOMETRY.plank, PLANK_MATERIAL, new THREE.Vector3(Math.cos(a) * 6, 7 + Math.random() * 5, Math.sin(a) * 6), 12);
    }
  }

  createBurst() {
    const p = this.position;
    for (let i = 0; i < 22; i++) {
      const color = EMBER_COLORS[i % EMBER_COLORS.length];
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 8 + Math.random() * 14;
      const ember = this.particle(GEOMETRY.ember, glowMaterial(color), new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.abs(Math.cos(phi)) * speed * 0.8 + Math.random() * 5,
        Math.sin(phi) * Math.sin(theta) * speed
      ), 12);
      ember.userData.ember = true;
      ember.scale.setScalar(0.6 + Math.random() * 0.8);
    }
    for (let i = 0; i < 4; i++) {
      const a = Math.random() * Math.PI * 2;
      this.particle(GEOMETRY.plank, PLANK_MATERIAL, new THREE.Vector3(Math.cos(a) * 7, 5 + Math.random() * 5, Math.sin(a) * 7), 12);
    }
    for (let i = 0; i < 6; i++) {
      const puff = this.add(new THREE.Mesh(GEOMETRY.smoke, new THREE.MeshLambertMaterial({ color: 0x8a8580, flatShading: true, transparent: true, opacity: 0, depthWrite: false })));
      puff.position.copy(p).add(new THREE.Vector3((Math.random() - 0.5) * 2, Math.random(), (Math.random() - 0.5) * 2));
      puff.userData.rise = 2 + Math.random() * 2.5;
      puff.userData.size = 1 + Math.random() * 0.9;
      this.smoke.push(puff);
    }
    this.flash = this.add(new THREE.Mesh(GEOMETRY.fireball, glowMaterial(0xffc66b)));
    this.flash.position.copy(p);
    this.core = this.add(new THREE.Mesh(GEOMETRY.fireball, glowMaterial(0xff5a10, 0.85)));
    this.core.position.copy(p);
  }

  update() {
    if (this.isComplete) return false;

    const now = performance.now();
    const progress = Math.min((now - this.startTime) / this.duration, 1);

    if (progress >= 1) {
      this.remove();
      this.isComplete = true;
      if (this.onComplete) {
        this.onComplete();
      }
      return false;
    }

    const deltaTime = Math.min((now - this.lastTime) / 1000, 0.05);
    this.lastTime = now;

    for (const particle of this.particles) {
      const v = particle.userData.velocity;
      particle.position.addScaledVector(v, deltaTime);
      v.y -= (particle.userData.water ? 26 : 22) * deltaTime;
      v.multiplyScalar(1 - 0.8 * deltaTime);
      particle.rotation.x += particle.userData.spin.x * deltaTime;
      particle.rotation.y += particle.userData.spin.y * deltaTime;
      particle.rotation.z += particle.userData.spin.z * deltaTime;
      // Droplets and planks vanish back into the sea
      if (this.onWater && particle.position.y < (this.surface ?? 0)) particle.visible = false;
      if (particle.userData.ember) {
        particle.material.opacity = 1 - Math.min(progress / 0.7, 1);
      }
    }
    if (this.dropletMaterial) this.dropletMaterial.opacity = 0.95 * (1 - Math.max(0, progress - 0.6) / 0.4);

    if (this.plumes) {
      // Blobs shoot up fast, hang, then slump back into the sea
      const rise = Math.sin(Math.min(progress / 0.75, 1) * Math.PI);
      for (const blob of this.plumes) {
        blob.position.y = (this.surface ?? 0) + rise * blob.userData.lift * 0.6;
        blob.scale.set(blob.userData.size * (1 + progress), blob.userData.size * (1 + rise * 0.8), blob.userData.size * (1 + progress));
      }
      this.plumeMaterial.opacity = 0.95 * (1 - Math.max(0, progress - 0.5) / 0.5);
      for (const ring of this.rings) {
        const t = Math.max(0, (progress - ring.userData.delay) / (1 - ring.userData.delay));
        ring.scale.setScalar(1 + (1 - Math.pow(1 - t, 2)) * 7);
        ring.material.opacity = 0.9 * (1 - t);
      }
    }

    for (const puff of this.smoke) {
      puff.position.y += puff.userData.rise * deltaTime;
      puff.rotation.y += 0.6 * deltaTime;
      puff.scale.setScalar(puff.userData.size * (0.6 + progress * 1.8));
      puff.material.opacity = 0.6 * Math.min(1, progress * 4) * (1 - progress);
    }

    if (this.flash) {
      this.flash.material.opacity = Math.max(0, 1 - progress * (this.onWater ? 6 : 2.5));
      this.flash.scale.setScalar(0.6 + progress * (this.onWater ? 2 : 3));
    }
    if (this.core) {
      this.core.material.opacity = 0.85 * Math.max(0, 1 - progress * 1.4);
      this.core.scale.setScalar(0.8 + progress * 1.6);
    }

    return true;
  }

  remove() {
    // Shared geometries stay alive; only per-explosion materials are disposed
    const materials = new Set();
    for (const object of this.objects) {
      this.scene.remove(object);
      if (object.material !== PLANK_MATERIAL) materials.add(object.material);
    }
    materials.forEach(material => material.dispose());
    this.objects = [];
    this.particles = [];
    this.smoke = [];
  }
}
